import type { HTMLAttributes } from 'react';
import { styled, alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import Badge from '@mui/material/Badge';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import React, { useRef } from 'react';
import useKeyboardEventHandler from 'hooks/useKeyboardEventHandler';

const useStyles = makeStyles()(() => ({
  advancedSearch: {
    position: 'absolute',
    top: -5,
    right: 10,
  },
  clearSearch: {
    position: 'absolute',
    top: -5,
    right: 40,
  },
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mode === 'light'
      ? alpha(theme.palette.common.black, 0.05)
      : alpha(theme.palette.common.white, 0.05),
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? alpha(theme.palette.common.black, 0.15)
        : alpha(theme.palette.common.white, 0.15),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('xs')]: {
      width: '10ch',
    },
    [theme.breakpoints.up('sm')]: {
      width: '30ch',
    },
  },
}));

interface Props extends HTMLAttributes<HTMLDivElement> {
  advSearchFilterCount: number;
  onClearClick: () => void;
  onEnterKeyPress?: (searchText: string) => void;
  setShowAdvSearchDialog: (show: boolean) => void;
  value: string;
}

export default function SearchWidget({
  advSearchFilterCount,
  onClearClick,
  onEnterKeyPress,
  setShowAdvSearchDialog,
  value,
}: Props) {
  const { classes } = useStyles();
  const searchInput = useRef<HTMLInputElement | null>(null);
  const handleClearClick = () => {
    onClearClick();

    if (searchInput.current) {
      searchInput.current.focus();
    }
  };
  const handleKeypress = (key: string) => {
    switch (key) {
      case 'enter':
        if (onEnterKeyPress) {
          onEnterKeyPress(value);
        }
        break;
      case 'escape':
        handleClearClick();
        break;
      default:
        break;
    }
  };

  useKeyboardEventHandler({
    keys: ['enter', 'escape'],
    handleFocusableElements: true,
    onKeyEvent: handleKeypress,
  });

  return (
    <Search>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      <StyledInputBase
        placeholder="Search…"
        // @ts-expect-error issue existed before automated typechecking
        slotProps={{ htmlInput: { 'aria-label': 'search' } }}
        inputRef={searchInput}
        autoFocus
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (onEnterKeyPress) {
            onEnterKeyPress(event.target.value);
          }
        }}
      />

      <Tooltip title="Advanced Search">
        <IconButton
          size="large"
          edge="end"
          color="inherit"
          aria-label="advanced-search-button"
          className={classes.advancedSearch}
          onClick={() => setShowAdvSearchDialog(true)}
        >
          <Badge badgeContent={advSearchFilterCount} color="error">
            <Icon>filter_alt</Icon>
          </Badge>
        </IconButton>
      </Tooltip>

      {value !== '' && (
        <Tooltip title="Clear">
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="advanced-search-button"
            className={classes.clearSearch}
            onClick={handleClearClick}
          >
            <Icon>clear_all</Icon>
          </IconButton>
        </Tooltip>
      )}
    </Search>
  );
}
